import app from '../app';
import Component, { ComponentAttrs } from '../../common/Component';
import Link from '../../common/components/Link';
import avatar from '../../common/helpers/avatar';
import listItems from '../../common/helpers/listItems';
import highlight from '../../common/helpers/highlight';
import icon from '../../common/helpers/icon';
import humanTime from '../../common/utils/humanTime';
import ItemList from '../../common/utils/ItemList';
import abbreviateNumber from '../../common/utils/abbreviateNumber';
import TerminalPost from '../components/TerminalPost';
import SubtreeRetainer from '../../common/utils/SubtreeRetainer';
import DiscussionControls from '../utils/DiscussionControls';
import classList from '../../common/utils/classList';
import DiscussionPage from '../components/DiscussionPage';
import escapeRegExp from '../../common/utils/escapeRegExp';
import Tooltip from '../../common/components/Tooltip';
import type Discussion from '../../common/models/Discussion';
import type Mithril from 'mithril';
import type { DiscussionListParams } from '../states/DiscussionListState';
// import tagsLabel from 'flarum/tags/helpers/tagsLabel';
import SplitDropdown from '../../common/components/SplitDropdown';
import Button from '../../common/components/Button';
import LinkButton from '../../common/components/LinkButton';
import LevelConf from '../../common/models/LevelConf';
import UserCard from '../components/UserCard';
import LogInModal from '../components/LogInModal';
import DiscussionsUserPage from '../components/DiscussionsUserPage';
import { translate } from '../utils/translate';
export interface IDiscussionListItemAttrs extends ComponentAttrs {
  discussion: Discussion;
  params: DiscussionListParams;
}

/**
 * The `DlDiscussionListItem` component shows a single discussion in the
 * discussion list.
 */
export default class DlDiscussionListItem<CustomAttrs extends IDiscussionListItemAttrs = IDiscussionListItemAttrs> extends Component<CustomAttrs> {
  /**
   * Ensures that the discussion will not be redrawn
   * unless new data comes in.
   */
  subtree!: SubtreeRetainer;

  highlightRegExp?: RegExp;
  lazyLoad = false;
  contentKey: number = 0;
  selectVal: string | null = 'null';
  postBodyDom: Element | null = null;
  titleDom: Element | null = null;
  selectOptions = {
    null: app.translator.trans('dl-response.forum.warn.translate'),
    english: 'English',
    chinese_simplified: '简体中文',
    chinese_traditional: '繁體中文',
    // japanese: '日本語',
    korean: '한국인',
    portuguese: 'Português',
    spanish: 'español',
  };
  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.subtree = new SubtreeRetainer(
      () => this.attrs.discussion.freshness,
      () => {
        const time = app.session.user && app.session.user.markedAllAsReadAt();
        return time && time.getTime();
      },
      () => this.active()
    );
  }


  elementAttrs() {
    return {
      className: classList('DiscussionListItem', {
        active: this.active(),
        'DiscussionListItem--hidden': this.attrs.discussion.isHidden(),
        Slidable: 'ontouchstart' in window,
      }),
    };
  }

  view() {
    const discussion = this.attrs.discussion;

    const controls = DiscussionControls.controls(discussion, this).toArray();
    const attrs = this.elementAttrs();
    return (
      <div {...attrs}>
        {this.slidableUnderneathView()}
        {this.contentView()}
      </div>
    );
  }

  controlsView(controls: Mithril.ChildArray): Mithril.Children {
    return (
      !!controls.length && (
        <div className="DiscussionListItem-extra-list">
          <SplitDropdown
            icon="fas fa-ellipsis-v"
            className="ell-App-primaryControl"
            buttonClassName="Button--primary"
            accessibleToggleLabel={app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')}
          >
            {controls}
          </SplitDropdown>
        </div>
      )
    );
  }

  slidableUnderneathView(): Mithril.Children {
    const discussion = this.attrs.discussion;
    const isUnread = discussion.isUnread();

    return (
      <span
        className={classList('Slidable-underneath Slidable-underneath--left Slidable-underneath--elastic', { disabled: !isUnread })}
        onclick={this.markAsRead.bind(this)}
      >
        {icon('fas fa-check')}
      </span>
    );
  }

  contentView(): Mithril.Children {
    const discussion = this.attrs.discussion;
    const isUnread = discussion.isUnread();
    const isRead = discussion.isRead();
    return (
      <div>
        <div className={classList('DiscussionListItem-content', { unread: isUnread, read: isRead })}>{this.mainView()}</div>
        {this.replyCountItem()}
        {!app.current.matches(DiscussionsUserPage) && this.extraPostsItem()}
      </div>
    );
  }

  extraPostsItem() {
    const discussion = this.attrs.discussion;
    const user = discussion.user();
    let firstPost = discussion.firstPost();
    let mostRelevantPost = discussion.mostRelevantPost();
    let mostRelevantPost2 = discussion.mostRelevantPost2();
    let mostRelevantPost3 = discussion.mostRelevantPost3();
    let extraList = [];
    if (mostRelevantPost && firstPost && mostRelevantPost2 && mostRelevantPost3) {
      if (mostRelevantPost.id() != firstPost.id()) {
        extraList.push(mostRelevantPost);
      }
      if (mostRelevantPost.id() != mostRelevantPost2.id()) {
        extraList.push(mostRelevantPost2);
      }
      if (mostRelevantPost2.id() != mostRelevantPost3.id()) {
        extraList.push(mostRelevantPost3);
      }
    }
    
    return extraList.length > 0 && (
      <>
        <div className="DiscussionList-reply-box">
          {extraList.map((item, index) => {
            let jumpTo = this.getJumpTo(item);
            let postContent = String(item.contentHtml());
            let pTagPattern = /<p[^>]*>|<\/p>/gi;
            if (this.highlightRegExp) {
              postContent = highlight(postContent, this.highlightRegExp).children;
              let decodedText = postContent.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
              postContent = decodedText.replace(pTagPattern, '');
            }
            return (
              <Link href={app.route.discussion(discussion, jumpTo)} className="DiscussionList-reply-item">
                <div className="left">
                  {avatar(item.user() || null, { title: '' })}
                  {/* <div className='Avatar'><img src={item.user().avatarUrl()} /></div> */}
                </div>
                <div className="right">
                  <span className="name">{item.user()?.nickname()}</span>
                  <span className="reply-text">{postContent && m.trust(postContent)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </>
    );
  }

  authorAvatarView(): Mithril.Children {
    const discussion = this.attrs.discussion;
    const user = discussion.user();
    return (
      <Tooltip
        text={app.translator.trans('core.forum.discussion_list.started_text', { username: user.nickname(), ago: humanTime(discussion.createdAt()) })}
        position="right"
      >
        <Link className="DiscussionListItem-author" href={user ? app.route.user(user) : '#'} onclick={(event)=>{
          if (!app.session.user) {
            event.preventDefault();
            app.modal.show(LogInModal);
            return;
          }
        }}>
          {avatar(user || null, { title: '' })}
        </Link>
      </Tooltip>
    );
  }

  badgesView(): Mithril.Children {
    const discussion = this.attrs.discussion;

    return <ul className="DiscussionListItem-badges badges">{listItems(discussion.badges().toArray())}</ul>;
  }

  mainView(): Mithril.Children {
    const discussion = this.attrs.discussion;
    const jumpTo = this.getJumpTo(discussion.firstPost());
    const user = discussion.user();
    
    // 用户信息的组装时从缓存里面取的 刷新过后就有可能会没掉，这边处理方式从discussion里直接返 level_conf表的id和level需要一致
    let levelConf = user.userLevel().levelConf();
    let firstPost = String(discussion.firstPost() && discussion.firstPost().contentHtml());
    // 使用正则表达式匹配<img>标签
    let imgTagPattern = /<IMG\s[^>]*>/gi;
    // 使用replace方法替换匹配到的<img>标签为【图片】文本
    firstPost = firstPost.replace(imgTagPattern, app.translator.trans('dl-pay.forum.operation.img'));
    let pTagPattern = /<p[^>]*>|<\/p>/gi;
    if (this.highlightRegExp) {
      firstPost = highlight(firstPost, this.highlightRegExp).children;
      let decodedText = firstPost.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      firstPost = decodedText.replace(pTagPattern, '');
    }
    let isActivity = false;
    let activityStatus = '';

    if (discussion.pkgActivity()) {
      isActivity = true;
      activityStatus = discussion.pkgActivity()?.status();
    }
    let levelImg = undefined;
    let levelImgs;
    let levelImgRes;
    let levelColor;
    let levelColorRes;
    levelImgs =  levelConf?.levelImg();
    if (levelImgs) {
      levelImgRes  = JSON.parse(levelImgs);
      levelImg = levelImgRes[app.data.pkgLogo.id];
    }
    let levelColors =  levelConf?.color();
    if (levelColors) {
      levelColorRes  = JSON.parse(levelColors);
      levelColor = levelColorRes[app.data.pkgLogo.id];
    }
    return (
      <Link key={this.contentKey} href={app.route.discussion(discussion, jumpTo)} className="DiscussionListItem-main">
        <div className="DiscussionListItem-texts">
          <div className="DiscussionListItem-texts-left">
            {this.authorAvatarView()}
            <div className="DiscussionListItem-text">
              <div className="DiscussionListItem-userinfo">
                <p className="DiscussionListItem-username">{user.nickname()}</p>
                {user.badges().toArray().length > 0 && <ul className="DiscussionListItem-badges badges">{listItems(user.badges().toArray())}</ul>}
                <div className="DiscussionListItem-img-box">
                  <img className="DiscussionListItem-img" src={levelImg} />
                  <span
                    className="DiscussionListItem-img-lv"
                    style={`text-shadow: -1px -1px 0 ${levelColor}, 1px -1px 0 ${levelColor}, -1px 1px 0 ${levelColor}, 1px 1px 0 ${levelColor};`}
                  >
                    {levelConf?.level()}
                  </span>
                </div>
              </div>
              <div className="DiscussionListItem-state">
                <p className="DiscussionListItem-time">{humanTime(discussion.createdAt())}</p>
                {this.badgesView()}
              </div>
            </div>
          </div>

          <span className="DiscussionListItem-tag">{listItems(this.tagItems().toArray())}</span>
        </div>
        {isActivity && 
        <h2 className="DiscussionListItem-title">[{activityStatus}]</h2>
        }
        
        <h2 className="DiscussionListItem-title">{highlight(discussion.title(), this.highlightRegExp)}</h2>
        {/* <p className="DiscussionListItem-content-text"></p> */}
        <p className="DiscussionListItem-content-text">{firstPost && m.trust(firstPost)}</p>
        {discussion.mainImg() && (
          <div className="DiscussionListItem-content-images"> 
            <div className="DiscussionListItem-content-image-container">
              <img className={`containerImage ${this.lazyLoad?'':'lazy'}`} onload={()=>{
                  this.lazyLoad = true
                  }} loading="lazy" src={discussion.mainImg()} />
              <img className={this.lazyLoad?'lazy-img-no':'lazy-img'} src={`${app.data.pkgLogo.cdnUrl}/${app.data.pkgLogo.ossFile}/lazy.svg`}></img>
              <button className="containerImageBtn" style="display:none;">
                <img src={`${app.data.pkgLogo.cdnUrl}/${app.data.pkgLogo.ossFile}/img-lbtuqh.png`}></img>
              </button>
            </div>
          </div>
        )}
      </Link>
    );
  }

  getJumpTo(post: any) {
    const discussion = this.attrs.discussion;
    let jumpTo = 0;

    if (this.attrs.params.q) {
      const post = discussion.mostRelevantPost();
      if (post) {
        jumpTo = post.number();
      }
      const phrase = escapeRegExp(this.attrs.params.q);
      this.highlightRegExp = new RegExp(phrase + '|' + phrase.trim().replace(/\s+/g, '|'), 'gi');
    } else {
      jumpTo = Math.min(discussion.lastPostNumber() ?? 0, (discussion.lastReadPostNumber() || 0) + 1);
    }

    return jumpTo;
  }

  oncreate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
    super.oncreate(vnode);
    this.setupImageListeners();
  }

  onupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
    super.onupdate(vnode);
    this.setupImageListeners();
  }

  setupImageListeners() {
    const images = this.element.querySelectorAll('.containerImage:not([data-img-setup])');
    const buttons = this.element.querySelectorAll('.containerImageBtn:not([data-btn-setup])');

    const handleImageSizing = (img: HTMLImageElement) => {
      if (img.naturalHeight > img.naturalWidth) {
        img.style.aspectRatio = '16/10';
        (img.parentNode as Element).classList.add('shaded');
        const btn = (img.parentNode as Element).querySelector('.containerImageBtn') as HTMLElement;
        if (btn) btn.style.display = 'flex';
      }
    };

    images.forEach((imgEl) => {
      const img = imgEl as HTMLImageElement;
      img.setAttribute('data-img-setup', 'true');
      // 处理浏览器缓存图片：缓存图片 load 事件可能在注册监听器前已触发
      if (img.complete) {
        handleImageSizing(img);
      }
      img.addEventListener('load', () => handleImageSizing(img));
    });

    buttons.forEach((btnEl) => {
      const button = btnEl as HTMLElement;
      button.setAttribute('data-btn-setup', 'true');
      button.addEventListener('click', function (this: HTMLElement, event) {
        event.stopPropagation();
        event.preventDefault();
        const img = (this.parentNode as Element).querySelector('.containerImage') as HTMLElement;
        if (img) img.style.aspectRatio = '0';
        this.style.display = 'none';
        if (img) (img.parentNode as Element).classList.remove('shaded');
      });
    });
  }

  onbeforeupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>) {
    super.onbeforeupdate(vnode);

    return this.subtree.needsRebuild();
  }

  /**
   * Determine whether or not the discussion is currently being viewed.
   */
  active() {
    return app.current.matches(DiscussionPage, { discussion: this.attrs.discussion });
  }

  /**
   * Determine whether or not information about who started the discussion
   * should be displayed instead of information about the most recent reply to
   * the discussion.
   */
  showFirstPost() {
    return ['newest', 'oldest'].includes(this.attrs.params.sort ?? '');
  }

  /**
   * Determine whether or not the number of replies should be shown instead of
   * the number of unread posts.
   *
   * @return {boolean}
   */
  showRepliesCount() {
    return this.attrs.params.sort === 'replies';
  }

  /**
   * Mark the discussion as read.
   */
  markAsRead() {
    const discussion = this.attrs.discussion;

    if (discussion.isUnread()) {
      discussion.save({ lastReadPostNumber: discussion.lastPostNumber() });
      m.redraw();
    }
  }

  /**
   * Build an item list of info for a discussion listing. By default this is
   * just the first/last post indicator.
   */
  infoItems(): ItemList<Mithril.Children> {
    const items = new ItemList<Mithril.Children>();

    if (this.attrs.params.q) {
      const post = this.attrs.discussion.mostRelevantPost() || this.attrs.discussion.firstPost();

      if (post && post.contentType() === 'comment') {
        const excerpt = highlight(post.contentPlain() ?? '', this.highlightRegExp, 175);
        items.add('excerpt', excerpt, -100);
      }
    } else {
      items.add('terminalPost', <TerminalPost discussion={this.attrs.discussion} lastPost={!this.showFirstPost()} />);
    }

    return items;
  }

  tagItems(): ItemList<Mithril.Children> {
    const items = new ItemList<Mithril.Children>();

    return items;
  }

  addLikeCount() {}

  //评论数
  replyCountItem() {
    const discussion = this.attrs.discussion;
    const user = discussion.user();
    let posts = discussion.posts();
    // let count = posts?.likes()?.length;
    // let likes = posts.likes()
    const controls = DiscussionControls.controls(discussion, this).toArray();
    // const showUnread = !this.showRepliesCount() && discussion.isUnread();
    // if (showUnread) {
    //   return (
    //     <button className="Button--ua-reset DiscussionListItem-count" onclick={this.markAsRead.bind(this)}>
    //       <span aria-hidden="true">{abbreviateNumber(discussion.unreadCount())}</span>
    //       <span className="visually-hidden">
    //         {app.translator.trans('core.forum.discussion_list.unread_replies_a11y_label', { count: discussion.replyCount() })}
    //       </span>
    //     </button>
    //   );
    // }
    return (
      <div className="Dl-DiscussionListItem">
        <div className="Dl-DiscussionListItem-left">
          <div className="item">
            <Button className="btn-icon bg-message" aria-label="message"></Button>
            <text>{abbreviateNumber(discussion.replyCount())}</text>
          </div>
          <div className="item">
            <Button className="btn-icon bg-like" aria-label="like"></Button>
            <text>{abbreviateNumber(discussion.likeCount())}</text>
          </div>
        </div>

        {this.controlsView(controls)}
      </div>
    );
  }
  onTranslate(e: any) {
    if (this.selectVal === e) return; // 如果选择的值没有变化，则不执行翻译操作
    this.selectVal = e;

    // 还原翻译：递增 contentKey 使 Mithril 销毁翻译态 DOM 并从 VDOM 重建，
    // 同时恢复原始内容和事件处理器
    if (e === 'null') {
      this.postBodyDom = null; // 清空缓存的 DOM 元素
      this.titleDom = null; // 清空缓存的 DOM 元素
      this.selectOptions['null'] = app.translator.trans('dl-response.forum.warn.translate'); // 重置选择值
      this.contentKey++;
      this.subtree.invalidate();
      return;
    }

    // 翻译操作
    const postElement = this.element; // 获取当前组件的根 DOM 元素
    const postBodyElements = postElement.getElementsByClassName('DiscussionListItem-content-text'); // 在当前组件范围内查找
    const titleElements = postElement.getElementsByClassName('DiscussionListItem-title');
    if (this.titleDom === null) {
      this.titleDom = titleElements[0].cloneNode(true) as Element;
    } else {
      // 切换翻译语言时，先用 innerHTML 恢复原文再重新翻译
      titleElements[0].innerHTML = this.titleDom.innerHTML;
    }
    if (this.postBodyDom === null) {
      this.postBodyDom = postBodyElements[0].cloneNode(true) as Element;
    } else {
      // 切换翻译语言时，先用 innerHTML 恢复原文再重新翻译
      postBodyElements[0].innerHTML = this.postBodyDom.innerHTML;
    }
    this.selectOptions['null'] = app.translator.trans('dl-response.forum.warn.cancel_translate');
    const originalQuerySelectorAll = document.querySelectorAll;
    document.querySelectorAll = function (selector) {
      const elements = originalQuerySelectorAll.call(this, selector);
      if (selector === 'iframe') {
        return Array.from(elements).filter((iframe) => !iframe.matches('#ah-web-sdk-iframe'));
      }
      return elements;
    };
    const documents = [postBodyElements[0], titleElements[0]]; // 转换为数组
    translate.language.setLocal(translate.language.recognition(this.postBodyDom.innerHTML + this.titleDom.innerHTML).languageName);
    translate.setDocuments(documents);
    translate.language.clearCacheLanguage();
    translate.changeLanguage(e);

    // const obj = {
    //   from: translate.language.recognition(this.postBodyDom.innerHTML).languageName,
    //   to: e,
    //   texts: [this.postBodyDom.innerHTML],
    // };
    // translate.request.translateText(obj, (data:any) => {
    //   if (data.text[0] !== postBodyElements[0].innerHTML) {
    //     translate.changeLanguage(e);
    //   }
    // });
  }
}
